import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectIsOwner } from 'containers/app/appSlice';
import { SubscriptionStatus } from 'containers/app/app.model';
import { GreyStrokeInfoIcon } from 'assets/icons';
import routesPath from 'routes/RoutesPath';
import GlobalBanner from './components/GlobalBanner';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { FSM_BANNER_ROUTES } from './constants';

/**
 * GlobalBannerRenderer - Displays contextual banners based on subscription states and user conditions
 *
 * Usage: Place this component at the top of your page layouts to show relevant subscription
 * banners like subscription ended, payment failed, trial ending, etc.
 *
 * Similar to GlobalModalRenderer but for banner notifications instead of modals.
 *
 * @param isFullScreenLayout - Set to true to hide banners on full-screen layouts
 */
interface GlobalBannerRendererProps {
  isFullScreenLayout?: boolean;
}

const GlobalBannerRenderer: FC<GlobalBannerRendererProps> = ({ isFullScreenLayout = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentTeam = useAppSelector(selectCurrentTeam);
  const isOwner = useAppSelector(selectIsOwner);

  const {
    isTrialPlan,
    hasPaymentFailed,
    isPaymentMethodUpdated,
    fsmPlanValidUntilDate,
    hasDowngradedAfterTrial,
    isCancelledPlan
  } = useFSMPlanInfo();

  // Estimates subscription ended banner
  const showEstimateSubscriptionEndedBanner =
    isOwner &&
    currentTeam?.subscriptionStatus === SubscriptionStatus.SUBSCRIPTION_EXPIRED &&
    location.pathname === routesPath.ESTIMATES;

  const showFSMBanners =
    isOwner &&
    (FSM_BANNER_ROUTES.includes(location.pathname) || location.pathname.includes(routesPath.ACCOUNTING_PAY_RUNS));

  const goToFSMBilling = () => {
    navigate(routesPath.FIELD_SERVICE_MANAGEMENT);
  };

  if (showEstimateSubscriptionEndedBanner) {
    return (
      <GlobalBanner
        icon={<GreyStrokeInfoIcon />}
        message="Your estimates subscription has ended"
        actionText="Manage subscription"
        onActionClick={() => navigate(routesPath.ESTIMATE_INTEGRATION)}
      />
    );
  }

  if (showFSMBanners) {
    if (isTrialPlan && !isPaymentMethodUpdated) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message="Your Field Serve Plus trial is active."
          subMessage="To keep Plus features after your trial ends, please add your billing info."
          actionText="Add billing info"
          onActionClick={goToFSMBilling}
        />
      );
    }
    if (hasPaymentFailed) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message="We couldn’t process your Field Serve Plus payment."
          subMessage={`Update billing by ${fsmPlanValidUntilDate} to avoid losing Plus features.`}
          actionText="Update billing info"
          onActionClick={goToFSMBilling}
        />
      );
    }
    // if (isTrialEnded && !isPaymentMethodUpdated) {
    //   return (
    //     <GlobalBanner
    //       icon={<GreyStrokeInfoIcon />}
    //       message="Your Field Serve Plus trial has ended."
    //       subMessage={`Add billing info by ${gracePeriodEndsAtDate} to keep Plus features for your team.`}
    //       actionText="Add billing info"
    //       onActionClick={goToFSMBilling}
    //     />
    //   );
    // }
    if (hasDowngradedAfterTrial) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message="Your Field Serve Plus trial has ended."
          subMessage="Your plan has switched to Field Serve. Upgrade to Plus to access premium features."
          actionText="Upgrade to Plus"
          onActionClick={goToFSMBilling}
        />
      );
    }
    if (isCancelledPlan) {
      return (
        <GlobalBanner
          icon={<GreyStrokeInfoIcon />}
          message="Your Field Serve Plus subscription is canceled."
          subMessage={`You’ll keep Plus features until ${fsmPlanValidUntilDate}. 
          After that, your plan will switch to Field Serve.`}
          actionText="Upgrade to Plus"
          onActionClick={goToFSMBilling}
        />
      );
    }
  }

  return null;
};

export default GlobalBannerRenderer;
